/**
 * shop-nav-wiring.js
 * ------------------
 * Wires fragno-global-nav events to existing shop components:
 * - Profile dropdown toggle on account-click
 * - Community selector opened via filters-cta click
 * - Mobile menu toggle
 * - Updates fragno-global-nav pill state after community selection
 * - Search typeahead, backdrop, and keyboard navigation
 * - Search zero state (popular stores, quick links) before typing (CL-22)
 */

import { dispatchTrack } from "../analytics/shop-analytics-track.js";
import { createRecentSearches, recentHref } from "./recent_searches.js";
import {
  createZeroState,
  isZeroStateClear,
  isZeroStateItem,
  parseZeroStateConfig,
} from "./search_zero_state.js";
import { searchResultHref } from "./search_result_href.js";
import { suggestionText } from "./search_suggestion_text.js";
import {
  suggestionCardSize,
  suggestionCardWrapCss,
  watchSuggestionCardSize,
} from "./search_suggestion_card.js";

let _initialized = false;

function initNavWiring({
  navigate = (url) => {
    window.location.href = url;
  },
} = {}) {
  if (_initialized) return;

  const globalNav = document.querySelector("fragno-global-nav");
  const profileDropdown = document.querySelector("shop-profile-dropdown");
  const communitySelector = document.querySelector("shop-community-selector");
  const mobileMenu = document.querySelector("shop-mobile-menu");

  if (!globalNav) return;

  _initialized = true;

  // ── Profile dropdown toggle ──
  if (profileDropdown) {
    globalNav.addEventListener("fragno-global-nav-account-click", () => {
      const isOpen = profileDropdown.hasAttribute("open");
      profileDropdown.toggleAttribute("open", !isOpen);
      if (!isOpen) {
        // Close search if open
        if (globalNav.hasAttribute("search-open")) {
          globalNav.removeAttribute("search-open");
        }
        const accountBtn = globalNav.querySelector(
          ".fragno-global-nav__avatar-trigger",
        );
        const menu = profileDropdown.querySelector(
          ".shop-profile-dropdown__menu",
        );
        if (accountBtn && menu) {
          const rect = accountBtn.getBoundingClientRect();
          menu.style.position = "fixed";
          menu.style.top = `${rect.bottom + 8}px`;
          menu.style.right = `${window.innerWidth - rect.right}px`;
          menu.style.left = "auto";
        }
      }
    });
    document.addEventListener("click", (e) => {
      if (
        !profileDropdown.contains(e.target) &&
        !globalNav.contains(e.target)
      ) {
        profileDropdown.removeAttribute("open");
      }
    });
  }

  // ── Community selector ──
  globalNav.addEventListener("fragno-global-nav-filters-click", () => {
    // Close search and profile dropdown when community selector opens
    if (globalNav.hasAttribute("search-open")) {
      globalNav.removeAttribute("search-open");
    }
    if (profileDropdown?.hasAttribute("open")) {
      profileDropdown.removeAttribute("open");
    }
    const anchor =
      globalNav.querySelector(".fragno-global-nav__filters-cta") || globalNav;
    document.dispatchEvent(
      new CustomEvent("open-community-selector", {
        bubbles: true,
        composed: true,
        detail: { anchor },
      }),
    );
  });

  document.addEventListener("click", (e) => {
    if (e.target?.closest?.("[data-community-add]")) {
      e.preventDefault();
      e.stopPropagation();
      document.dispatchEvent(
        new CustomEvent("open-community-selector", {
          bubbles: true,
          composed: true,
        }),
      );
    }
  });

  // ── Mobile menu toggle ──
  if (mobileMenu) {
    globalNav.addEventListener("fragno-global-nav-mobile-menu-toggle", (e) => {
      mobileMenu.toggleAttribute("open", e.detail?.open ?? false);
    });
  }

  // ── Inject styles ──
  const searchStyle = document.createElement("style");
  searchStyle.textContent = `
    /* ─── Z-index stacking for overlays ─── */
    fragno-global-nav.is-search-open,
    fragno-global-nav.is-overlay-active {
      position: relative;
      z-index: 92;
    }
    fragno-global-nav.is-search-open ~ shop-local-nav {
      z-index: 89 !important;
    }
    /* ─── Active state for "Shop" nav link ─── */
    fragno-global-nav .fragno-global-nav__link[href="/"] {
      color: var(--colors-green-900, #14532d);
      background-color: var(--colors-green-900-alpha-10, rgba(20, 83, 45, 0.1));
    }

    .shop-community-banner,
    fragno-ribbon-banner {
      position: relative;
      z-index: 91;
    }
    .fragno-global-nav--search-open .fragno-global-nav__shell {
      box-shadow: none !important;
      border-bottom-left-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
    }
    .fragno-global-nav--search-open .fragno-global-nav__bar {
      backdrop-filter: none !important;
      -webkit-backdrop-filter: none !important;
    }

    /* Hide fragno's own built-in "Clear" button — we render our own
       .shop-search-clear-btn below with our own styling/copy, and both
       become visible on the same condition (query present), so without
       this both show up at once. !important plus a per-render JS force in
       syncClearButton() (same belt-and-suspenders shape as the
       results-heading duplicate fix): fragno's own _syncSearchClear()
       re-toggles this element's native hidden property on every keystroke. */
    fragno-global-nav [data-ref="search-clear"] {
      display: none !important;
    }

    /* ─── #1/#2: Custom "Clear" button inside search field ─── */
    input[type="search"]::-webkit-search-cancel-button {
      -webkit-appearance: none;
      display: none;
    }
    .shop-search-clear-btn {
      display: none;
      align-items: center;
      background: none;
      border: none;
      cursor: pointer;
      font-size: var(--font-size-sm, 0.875rem);
      font-weight: 500;
      color: var(--colors-gray-900, #141414);
      text-decoration: underline;
      text-underline-offset: 0.25rem;
      padding: 0 var(--spacing-md, 0.5rem);
      white-space: nowrap;
      flex-shrink: 0;
    }
    .shop-search-clear-btn--visible {
      display: flex;
    }
    @media (max-width: 63.99rem) {
      /* SHOPWEB-1199: was force-hiding shop-web's OWN clear button on
         mobile, which left fragno's native [data-ref="search-clear"] as the
         only visible one there (undocumented reliance on the very duplicate
         we're now suppressing everywhere above). Removed so
         .shop-search-clear-btn shows consistently on every viewport instead
         of leaving mobile with no clear button once fragno's native one is
         hidden unconditionally. */
      fragno-global-nav[search-open] .fragno-global-nav__menu-button {
        display: none !important;
      }
      shop-profile-dropdown {
        display: none !important;
      }
      fragno-global-nav .fragno-global-nav__avatar-trigger {
        display: none !important;
      }
      fragno-global-nav fragno-avatar {
        display: none !important;
      }
      fragno-global-nav .fragno-global-nav__nav {
        display: none !important;
      }
      /* Hide fragno's built-in mobile drawer — shop-mobile-menu provides
         our own mobile navigation with category support. Exempt search-open:
         fragno reparents the live search input into this same drawer for its
         mobile search sheet, so hiding it unconditionally makes the input
         untappable/unfocusable once search opens on mobile. */
      fragno-global-nav:not(.is-search-open) .fragno-global-nav__mobile-drawer {
        display: none !important;
      }
      .shop-community-icon-stack {
        display: none !important;
      }
      /* Scale down fragno's native icon stack to fit the compact nav */
      .fragno-global-nav__filters-stack {
        gap: 0.125rem !important;
      }
      .fragno-global-nav__center-slot .fragno-global-nav__filters-cta .fragno-button {
        width: auto !important;
        padding: 0.25rem 0.5rem !important;
      }
      .fragno-global-nav__filters-stack-icon {
        width: 1.75rem !important;
        height: 1.75rem !important;
        padding: 0.25rem !important;
        box-sizing: border-box !important;
      }
      .fragno-global-nav__filters-stack-icon + .fragno-global-nav__filters-stack-icon {
        margin-left: -0.5rem !important;
      }
      .fragno-global-nav__filters-stack-icon img {
        width: 100% !important;
        height: 100% !important;
        object-fit: contain !important;
      }
    }

    /* ─── #5: Single column search results ─── */
    fragno-global-nav .shop-search-results__grid {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-xl, 1rem);
    }
    fragno-global-nav [data-part="search-results"] {
      margin-top: var(--spacing-sm, 0.375rem);
      max-height: 26rem;
      overflow-y: auto;
      overflow-x: hidden;
      padding: 0 var(--spacing-xs, 0.25rem);
    }
    fragno-global-nav [data-part="search-results"]:empty {
      display: none;
    }

    /* Hide fragno's own built-in "Results" heading — it has no count and
       duplicates our .shop-search-results__header, which does. Re-added
       !important (SHOPWEB-1199): shop_nav_wiring.js also force-sets the
       native hidden property on this element on every render as a second
       layer, but keep the CSS side maximally defensive too since fragno's
       _renderSearchPanel() re-toggles hidden on every keystroke and we'd
       rather not depend on exact cascade/specificity behavior surviving a
       future fragno bump. */
    fragno-global-nav [data-ref="results-heading"] {
      display: none !important;
    }

    /* ─── #12: Prevent focus ring cropping on results ─── */
    fragno-global-nav .shop-search-results__header {
      font-size: var(--font-size-sm, 0.875rem);
      font-weight: var(--font-weight-semibold, 600);
      color: var(--colors-gray-900, #141414);
      padding: var(--spacing-xs, 0.25rem) 0 var(--spacing-sm, 0.375rem);
    }

    /* ─── Search result cards ─── */
    /* Each result is a <fragno-offer-card card-style="outline">, the Figma "Offer Cards"
       component, so size, type and spacing come from fragno's tokens. Its size is "sm", or
       "xs" on phones (see search_suggestion_card.js). Fill the column instead of
       fragno's fixed 24rem. */
    fragno-global-nav .shop-search-result {
      --offer-card-sm-width: 100%;
      --offer-card-sm-min-width: 0px;
      --offer-card-sm-max-width: 100%;
      width: 100%;
      min-width: 0;
      max-width: 100%;
    }
    /* Text wraps instead of truncating (see search_suggestion_card.js). */
    ${suggestionCardWrapCss("fragno-global-nav .shop-search-result")}
    fragno-global-nav .shop-search-result--focused {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }

    /* ─── CL-22: zero state (before typing) ─── */
    /* Spacing and type follow the Figma (Global Nav v1.0): 32px between
       sections, 16px heading-to-pills (fragno's suggested-heading), 12px
       between pills, 36px pills on desktop and mobile. */
    fragno-global-nav .shop-search-zero-state {
      position: relative;
      padding: var(--spacing-xs, 0.25rem) var(--spacing-xs, 0.25rem) var(--spacing-md, 0.5rem);
    }
    fragno-global-nav .shop-search-zero-state[hidden] {
      display: none;
    }
    fragno-global-nav .shop-search-zero-state__list {
      display: flex;
      flex-direction: column;
      gap: var(--spacing-4xl, 2rem);
    }
    fragno-global-nav .shop-search-zero-state__items {
      display: flex;
      flex-wrap: wrap;
      gap: var(--spacing-lg, 0.75rem);
    }
    /* Recent searches carry a 20px clock icon, 4px from the text (Figma). */
    fragno-global-nav .shop-search-zero-state__item {
      box-sizing: border-box;
      height: 2.25rem;
      padding-block: 0;
      gap: var(--spacing-xs, 0.25rem);
    }
    /* Safety net for narrow screens: every pill's text is already cut at 30 characters,
       but the pill must never be wider than its container. */
    fragno-global-nav .shop-search-zero-state__item {
      max-width: 100%;
    }
    fragno-global-nav .shop-search-zero-state__text {
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    fragno-global-nav .shop-search-zero-state__icon {
      flex-shrink: 0;
    }
    /* Clear sits in the "Recent searches" heading row (not in the Figma, so
       design to confirm). It comes before the listbox in the DOM so it is
       also first in the tab order, and is placed here with CSS. */
    fragno-global-nav .shop-search-zero-state__clear {
      position: absolute;
      top: var(--spacing-xs, 0.25rem);
      right: var(--spacing-xs, 0.25rem);
      min-height: 1.5rem;
      padding: 0 var(--spacing-xxs, 0.125rem);
      border: 0;
      border-radius: var(--radius-sm, 0.25rem);
      background: none;
      color: var(--colors-gray-700, #424242);
      font-family: var(--font-family-body, inherit);
      font-size: var(--font-size-text-sm, 0.875rem);
      font-weight: var(--font-weight-medium, 500);
      line-height: 1.5rem;
      text-decoration: underline;
      cursor: pointer;
    }
    fragno-global-nav .shop-search-zero-state__clear:hover {
      color: var(--colors-gray-900, #141414);
    }
    fragno-global-nav .shop-search-zero-state__clear:focus-visible {
      outline: 2px solid var(--colors-focus-ring, #2563eb);
      outline-offset: 2px;
    }
    /* With up to 5 recent searches plus the suggestions this can be taller
       than a short window, and page scroll is locked while search is open,
       so on desktop the list scrolls inside the viewport. On mobile fragno's
       search sheet already scrolls. */
    @media (min-width: 64rem) {
      fragno-global-nav .shop-search-zero-state {
        max-height: calc(100dvh - 8.5rem);
        overflow-y: auto;
      }
    }

    /* ─── #10: Backdrop blocks interaction when search/overlay is open ─── */
    .shop-local-nav__dropdown-backdrop:not([hidden]) {
      pointer-events: auto;
    }

    /* ─── #11: No placeholder in search field ─── */
    .fragno-global-nav__search-input::placeholder {
      color: transparent;
    }

    /* ─── #17: Focus state wraps entire search field pill, 2px offset + 2px stroke ─── */
    .fragno-global-nav__search-input:focus-visible {
      outline: none !important;
    }
    .fragno-global-nav__search-field:focus-within {
      outline: 2px solid #2563eb;
      outline-offset: 2px;
    }

    /* ─── #8: Smoother transitions on nav elements during search ─── */
    .fragno-global-nav__nav,
    .fragno-global-nav__left,
    .fragno-global-nav__actions {
      transition: opacity 0.3s ease, visibility 0.3s ease;
    }

    /* ─── #13: Community icon stack in filters CTA ─── */
    /* Desktop: hide fragno's native icon-leading when our custom stack is present */
    fragno-global-nav fragno-button:has(.shop-community-icon-stack) .fragno-button__icon--leading {
      display: none;
    }
    /* Mobile: style fragno's native icon stack (green-900 bg, white icons) */
    .fragno-global-nav__filters-stack-icon {
      background: var(--colors-green-900, #14532d) !important;
    }
    .fragno-global-nav__filters-stack-icon img {
      filter: brightness(0) invert(1);
    }
    .fragno-global-nav__filters-stack-count {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 1.75rem;
      height: 1.75rem;
      border-radius: var(--radius-full, 9999px);
      padding: 0 0.25rem;
      box-sizing: border-box;
      margin-left: -0.5rem;
      border: 2px solid var(--color-bg-primary, #ffffff);
      background: var(--colors-green-900, #14532d) !important;
      color: #fff !important;
      font-size: 0.625rem !important;
      line-height: 1;
    }
    fragno-global-nav.is-search-open .shop-community-icon-stack {
      display: none;
    }
    .shop-community-icon-stack {
      display: flex;
      align-items: center;
    }
    .shop-community-icon-stack__icon {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 50%;
      background: var(--colors-green-900, #14532d);
      border: 2px solid var(--color-bg-primary, #ffffff);
      padding: 0.25rem;
      box-sizing: border-box;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      justify-content: center;
    }
    .shop-community-icon-stack__icon + .shop-community-icon-stack__icon {
      margin-left: -0.5rem;
    }
    .shop-community-icon-stack__icon img {
      width: 100%;
      height: 100%;
      object-fit: contain;
      filter: brightness(0) invert(1);
    }
    /* Single community selected: make the filters CTA a perfect circle.
       Native stack: 1 icon + no count badge = single community.
       Custom stack: 1 icon + no overflow = single community. */
    .fragno-global-nav__filters-cta:has(.fragno-global-nav__filters-stack-icon:only-child):not(:has(.fragno-global-nav__filters-stack-count)) .fragno-button,
    fragno-button:has(.shop-community-icon-stack__icon:only-child):not(:has(.shop-community-icon-stack__overflow)) .fragno-button {
      border-radius: 50% !important;
      aspect-ratio: 1 !important;
      padding: 0.375rem !important;
    }
    .shop-community-icon-stack__overflow {
      width: 1.75rem;
      height: 1.75rem;
      border-radius: 50%;
      background: var(--colors-green-900, #14532d);
      border: 2px solid var(--color-bg-primary, #ffffff);
      color: #fff;
      font-size: 0.6875rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-left: -0.5rem;
      flex-shrink: 0;
    }
  `;
  document.head.appendChild(searchStyle);

  // ── #13: Inject community icon stack into filters CTA button (desktop only) ──
  // On mobile, fragno 1.17.0 and 2.3.0 handle filters-cta-icons natively via
  // their own _buildFiltersIconStack. We only inject on desktop where we need
  // the green-900/white styling that fragno doesn't provide.
  function renderCommunityIcons() {
    if (globalNav.hasAttribute("search-open")) return;
    if (window.matchMedia("(max-width: 63.99rem)").matches) return;
    const iconsAttr = globalNav.getAttribute("filters-cta-icons");
    if (!iconsAttr) return;
    let urls;
    try {
      urls = JSON.parse(iconsAttr);
    } catch {
      return;
    }
    if (!Array.isArray(urls) || urls.length === 0) return;

    // Find the fragno-button for filters CTA
    const btn =
      globalNav.querySelector(".fragno-global-nav__filters-cta") ||
      globalNav.querySelector(
        ".fragno-global-nav__actions fragno-button, .fragno-global-nav__center-slot fragno-button",
      );
    if (!btn) return;

    // Don't duplicate
    if (btn.querySelector(".shop-community-icon-stack")) return;

    const maxShow = 2;
    const overflow = urls.length - maxShow;
    const stack = document.createElement("span");
    stack.className = "shop-community-icon-stack";

    urls.slice(0, maxShow).forEach((url) => {
      const icon = document.createElement("span");
      icon.className = "shop-community-icon-stack__icon";
      const img = document.createElement("img");
      img.src = url;
      img.alt = "";
      icon.appendChild(img);
      stack.appendChild(icon);
    });

    if (overflow > 0) {
      const badge = document.createElement("span");
      badge.className = "shop-community-icon-stack__overflow";
      badge.textContent = `+${overflow}`;
      stack.appendChild(badge);
    }

    // Insert before the text span inside the button
    const textSpan = btn.querySelector(".fragno-button__text");
    if (textSpan) {
      textSpan.parentNode.insertBefore(stack, textSpan);
    } else {
      btn.appendChild(stack);
    }
  }

  // On mobile, cap the icon stack to 1 visible icon so fragno renders
  // "icon + +N" instead of "icon icon + +N", freeing horizontal space.
  const MOBILE_MQ = window.matchMedia("(max-width: 63.99rem)");
  let fullIconUrls = null; // stash the full array for desktop restore

  function capMobileIcons() {
    const raw = globalNav.getAttribute("filters-cta-icons");
    if (!raw) return;
    let urls;
    try {
      urls = JSON.parse(raw);
    } catch {
      return;
    }
    if (!Array.isArray(urls) || urls.length <= 1) return;

    if (MOBILE_MQ.matches) {
      fullIconUrls = urls;
      // Rewrite attribute with only 1 icon — fragno will render 1 icon + "+N"
      ctaObserver.disconnect();
      globalNav.setAttribute(
        "filters-cta-icons",
        JSON.stringify(urls.slice(0, 1)),
      );
      ctaObserver.observe(globalNav, {
        childList: true,
        subtree: true,
        attributeFilter: ["filters-cta-icons"],
      });
    } else if (fullIconUrls) {
      // Restore full set on desktop
      ctaObserver.disconnect();
      globalNav.setAttribute("filters-cta-icons", JSON.stringify(fullIconUrls));
      fullIconUrls = null;
      ctaObserver.observe(globalNav, {
        childList: true,
        subtree: true,
        attributeFilter: ["filters-cta-icons"],
      });
    }
  }

  MOBILE_MQ.addEventListener("change", capMobileIcons);

  // Run after fragno renders (may be async), and re-run on attribute changes
  const ctaObserver = new MutationObserver(() => {
    capMobileIcons();
    renderCommunityIcons();
  });
  ctaObserver.observe(globalNav, {
    childList: true,
    subtree: true,
    attributeFilter: ["filters-cta-icons"],
  });
  // Initial render attempt
  requestAnimationFrame(() => {
    capMobileIcons();
    renderCommunityIcons();
  });

  // ── Search state ──
  let searchDebounce = null;
  let searchAbort = null;
  let searchResults = [];
  let searchSelectedIndex = -1;
  let lastSearchQuery = "";

  // ── CL-22: zero state (before typing) ──
  // Only active when the header renders data-zero-state (search_zero_state flag).
  // Lives inside fragno's panel-inner so fragno carries it into the mobile
  // search sheet along with the rest of the panel.
  const zeroStateConfig = parseZeroStateConfig(globalNav.dataset.zeroState);
  let zeroState = null;
  let zeroStateImpressionSent = false;

  // Recent searches live in this browser's localStorage, scoped to whoever is
  // signed in (the header passes the scope). Without a scope they are off.
  const recentSearches = zeroStateConfig?.recentScope
    ? createRecentSearches({ scope: zeroStateConfig.recentScope })
    : null;
  let renderedRecent = [];

  function storeEntry(store) {
    return { kind: "store", text: store.name, id: store.id };
  }

  function recordRecent(entry) {
    recentSearches?.add(entry);
  }

  // Re-read the recent searches and redraw, so a search made on an earlier
  // page (or in another tab) shows the next time the overlay opens.
  function refreshZeroState() {
    if (!zeroState) return;
    renderedRecent = recentSearches ? recentSearches.list() : [];
    zeroState.render({
      recent: renderedRecent.map((entry) => ({
        text: entry.text,
        href: recentHref(entry),
      })),
      popular: zeroStateConfig.popular,
      quickLinks: zeroStateConfig.quickLinks,
    });
  }

  function ensureZeroState() {
    if (!zeroStateConfig) return null;
    if (zeroState?.el.isConnected) return zeroState;
    const panelInner = globalNav.querySelector(
      ".fragno-global-nav__panel-inner",
    );
    if (!panelInner) return null;
    if (!zeroState) {
      zeroState = createZeroState({ labels: zeroStateConfig.labels });
      refreshZeroState();
      zeroState.el.addEventListener("click", (e) => {
        const clearBtn = e.target.closest?.("button");
        if (isZeroStateClear(clearBtn)) {
          clearRecent();
          return;
        }
        const item = e.target.closest?.(".shop-search-zero-state__item");
        if (item) selectZeroStateItem(item);
      });
    }
    panelInner.prepend(zeroState.el);
    return zeroState;
  }

  function isZeroStateVisible() {
    return Boolean(zeroState?.el.isConnected && !zeroState.el.hidden);
  }

  // The search input is fragno's bare <input type="search">. For a screen
  // reader to follow aria-activedescendant it needs the combobox pattern
  // (ARIA APG, editable combobox with list autocomplete): role="combobox",
  // aria-autocomplete, aria-expanded, and aria-controls pointing at the list
  // that holds the active option. That list is the zero state before typing
  // and the results grid after, so keep aria-controls on whichever is showing.
  const RESULTS_LIST_ID = "shop-search-results";

  function visibleListId(open) {
    if (!open) return null;
    if (isZeroStateVisible()) return zeroState.listEl.id;
    return globalNav.querySelector(`#${RESULTS_LIST_ID}`)
      ? RESULTS_LIST_ID
      : null;
  }

  function syncCombobox(open = globalNav.hasAttribute("search-open")) {
    const input = globalNav.querySelector(".fragno-global-nav__search-input");
    if (!input) return;
    const listId = visibleListId(open);
    input.setAttribute("role", "combobox");
    input.setAttribute("aria-autocomplete", "list");
    input.setAttribute("aria-expanded", String(Boolean(listId)));
    if (listId) input.setAttribute("aria-controls", listId);
    else input.removeAttribute("aria-controls");
  }

  function syncZeroState(query) {
    if (!zeroState) return;
    const show =
      globalNav.hasAttribute("search-open") &&
      query.length < 2 &&
      zeroState.hasItems();
    if (zeroState.el.hidden !== !show) {
      zeroState.el.hidden = !show;
      clearSelection();
      if (show && !zeroStateImpressionSent) {
        zeroStateImpressionSent = true;
        const counts = zeroState.counts();
        dispatchTrack(zeroState.el, "search_zero_state_impression", {
          recent_count: counts.recent,
          popular_count: counts.popular,
          quick_link_count: counts.quick_link,
        });
      }
    }
    syncCombobox();
  }

  function trackZeroStateSelect(item) {
    dispatchTrack(item, "search_suggestion_click", {
      source: "zero_state",
      group: item.dataset.group,
      position: Number(item.dataset.position),
      // A cut pill keeps its whole text in aria-label.
      label: item.getAttribute("aria-label") || item.textContent,
    });
  }

  // A pill was chosen (click or Enter): count it, and move a recent search back
  // to the top of the list. Popular stores and quick links are not saved.
  function selectZeroStateItem(item) {
    trackZeroStateSelect(item);
    if (item.dataset.group !== "recent") return;
    const entry = renderedRecent[Number(item.dataset.position) - 1];
    if (entry) recordRecent(entry);
  }

  function clearRecent() {
    const count = renderedRecent.length;
    recentSearches?.clear();
    dispatchTrack(zeroState.el, "search_recent_clear", { count });
    refreshZeroState();
    clearSelection();
    const input = globalNav.querySelector(".fragno-global-nav__search-input");
    syncZeroState(input?.value.trim() || "");
    // The Clear button is gone now, so keep keyboard focus in the search field.
    input?.focus();
  }

  // Arrow keys move through whichever list is showing: the zero state pills
  // before typing, the result cards after.
  function getNavigableItems() {
    if (isZeroStateVisible()) return zeroState.items();
    const grid = globalNav.querySelector(".shop-search-results__grid");
    return grid ? Array.from(grid.querySelectorAll("[role='option']")) : [];
  }

  function setSelectedIndex(index) {
    searchSelectedIndex = index;
    const items = getNavigableItems();
    items.forEach((el, i) =>
      el.classList.toggle("shop-search-result--focused", i === index),
    );
    const input = globalNav.querySelector(".fragno-global-nav__search-input");
    if (!input) return;
    if (index >= 0 && items[index]) {
      input.setAttribute("aria-activedescendant", items[index].id);
      // The list can be longer than the viewport; keep the focused pill visible.
      if (isZeroStateItem(items[index])) {
        items[index].scrollIntoView?.({ block: "nearest" });
      }
    } else {
      input.removeAttribute("aria-activedescendant");
    }
  }

  function clearSelection() {
    searchSelectedIndex = -1;
    globalNav
      .querySelectorAll(".shop-search-result--focused")
      .forEach((el) => el.classList.remove("shop-search-result--focused"));
    globalNav
      .querySelector(".fragno-global-nav__search-input")
      ?.removeAttribute("aria-activedescendant");
  }

  function getResultsPanel() {
    return globalNav.querySelector("[data-part='search-results']");
  }

  // Belt-and-suspenders alongside the CSS override in the injected <style>
  // block (search for [data-ref="results-heading"] { display: none; }):
  // fragno's own _renderSearchPanel() flips this element's native `hidden`
  // property on every keystroke (hidden = !hasQuery), so it's forced back
  // here on every shop-web render rather than relying on the CSS rule alone
  // to keep winning the cascade for the lifetime of the search session.
  function hideNativeResultsHeading() {
    const heading = globalNav.querySelector("[data-ref='results-heading']");
    if (heading) heading.hidden = true;
  }

  function getOrCreateBackdrop() {
    let backdrop = document.body.querySelector(
      ":scope > .shop-local-nav__dropdown-backdrop",
    );
    if (!backdrop) {
      backdrop = document.createElement("div");
      backdrop.className = "shop-local-nav__dropdown-backdrop";
      backdrop.setAttribute("hidden", "");
      backdrop.setAttribute("aria-hidden", "true");
      document.body.appendChild(backdrop);
    }
    return backdrop;
  }

  // ── #4: Click on backdrop closes search ──
  document.addEventListener("click", (e) => {
    if (
      e.target?.classList?.contains("shop-local-nav__dropdown-backdrop") &&
      globalNav.classList.contains("is-search-open")
    ) {
      globalNav.removeAttribute("search-open");
      globalNav.dispatchEvent(
        new CustomEvent("fragno-global-nav-search-toggle", {
          detail: { open: false },
          bubbles: true,
          composed: true,
        }),
      );
    }
  });

  let searchTotalCount = 0;
  let searchId = null;
  let searchVersion = null;

  const searchHref = (store) =>
    searchResultHref(store.url, searchId, searchVersion);

  // Result rows are the same <fragno-offer-card> the design uses. card-href makes fragno wrap
  // the card in a real link, so clicks, middle-click and open-in-new-tab keep working. The
  // .shop-search-result class and role="option" are what keyboard selection and the click
  // tracking below look for.
  function buildSearchResultCard(store, index) {
    // CL-23: the subtitle is the member's top offer, or the cash back rate when there is none.
    const { subtitle } = suggestionText(store);

    const card = document.createElement("fragno-offer-card");
    card.className = "shop-search-result";
    card.id = `search-result-${index}`;
    card.setAttribute("role", "option");
    card.setAttribute("data-index", String(index));
    card.setAttribute("title", store.name || "");
    if (subtitle) card.setAttribute("subtitle", subtitle);
    if (store.logo_attachment) {
      card.setAttribute("brand-logo", store.logo_attachment);
    }
    card.setAttribute("size", suggestionCardSize());
    card.setAttribute("card-style", "outline");
    card.setAttribute("card-href", searchHref(store) || "#");
    card.setAttribute("show-action-icon", "true");
    card.setAttribute("action-icon", "chevron");
    return card;
  }

  function renderSearchResults(stores) {
    hideNativeResultsHeading();
    const resultsPanel = getResultsPanel();
    if (!resultsPanel) return;
    resultsPanel.replaceChildren();
    if (stores.length === 0) {
      syncCombobox();
      return;
    }

    const header = document.createElement("div");
    header.className = "shop-search-results__header";
    header.textContent = `Results (${searchTotalCount})`;

    const grid = document.createElement("div");
    grid.className = "shop-search-results__grid";
    grid.id = RESULTS_LIST_ID;
    grid.setAttribute("role", "listbox");
    stores.forEach((store, i) =>
      grid.appendChild(buildSearchResultCard(store, i)),
    );

    resultsPanel.appendChild(header);
    resultsPanel.appendChild(grid);
    syncCombobox();
  }

  function fetchSearch(query) {
    clearTimeout(searchDebounce);
    searchAbort?.abort();
    clearSelection();
    lastSearchQuery = query;
    syncZeroState(query || "");
    if (!query || query.length < 2) {
      searchResults = [];
      renderSearchResults([]);
      return;
    }
    searchDebounce = setTimeout(async () => {
      searchAbort = new AbortController();
      try {
        const resp = await fetch(
          `/stores.json?q=${encodeURIComponent(query)}`,
          {
            headers: { Accept: "application/json" },
            credentials: "include",
            signal: searchAbort.signal,
          },
        );
        if (!resp.ok) return;
        const data = await resp.json();
        searchTotalCount = data.total_count || (data.results || []).length;
        searchId = typeof data.search_id === "string" ? data.search_id : null;
        searchVersion =
          typeof data.query_version === "string" ? data.query_version : null;
        searchResults = (data.results || []).slice(0, 5);
        renderSearchResults(searchResults);
      } catch (e) {
        if (e.name !== "AbortError") renderSearchResults([]);
      }
    }, 200);
  }

  // ── #1/#2: Inject custom clear button into search field ──
  function ensureClearButton() {
    if (globalNav.querySelector(".shop-search-clear-btn")) return;
    const searchField = globalNav.querySelector(
      ".fragno-global-nav__search-field",
    );
    if (!searchField) return;
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "shop-search-clear-btn";
    btn.textContent = "Clear";
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      const input = globalNav.querySelector(".fragno-global-nav__search-input");
      if (input) {
        input.value = "";
        input.focus();
        input.dispatchEvent(new Event("input", { bubbles: true }));
      }
      btn.classList.remove("shop-search-clear-btn--visible");
      searchResults = [];
      lastSearchQuery = "";
      renderSearchResults([]);
      syncZeroState("");
    });
    searchField.appendChild(btn);
  }

  // Belt-and-suspenders alongside the CSS override above, same shape as
  // hideNativeResultsHeading(): fragno's own _syncSearchClear() toggles this
  // element's native hidden property on every input event
  // (hidden = !this._hasSearchQuery()), so force it back here too rather
  // than depending on the CSS rule alone to keep winning the cascade.
  function hideNativeSearchClear() {
    const btn = globalNav.querySelector("[data-ref='search-clear']");
    if (btn) btn.hidden = true;
  }

  function syncClearButton() {
    hideNativeSearchClear();
    const input = globalNav.querySelector(".fragno-global-nav__search-input");
    const btn = globalNav.querySelector(".shop-search-clear-btn");
    if (!input || !btn) return;
    btn.classList.toggle(
      "shop-search-clear-btn--visible",
      input.value.trim().length > 0,
    );
  }

  // ── Search input events ──
  globalNav.addEventListener("fragno-global-nav-search-input", (e) => {
    const query = (e.detail?.value || "").trim();
    fetchSearch(query);
    syncClearButton();
  });

  // A store picked from the typed results is remembered as a recent search.
  globalNav.addEventListener("click", (e) => {
    const card = e.target.closest?.(".shop-search-result");
    const store = card && searchResults[Number(card.dataset.index)];
    if (store) recordRecent(storeEntry(store));
  });

  // Cards already showing follow the screen into or out of the compact size.
  watchSuggestionCardSize((size) => {
    globalNav
      .querySelectorAll(".shop-search-result")
      .forEach((card) => card.setAttribute("size", size));
  });

  globalNav.addEventListener("keydown", (e) => {
    if (!e.target?.classList?.contains("fragno-global-nav__search-input"))
      return;
    if (e.key === "Enter") {
      e.preventDefault();
      const selected = getNavigableItems()[searchSelectedIndex];
      if (selected && isZeroStateItem(selected)) {
        selectZeroStateItem(selected);
        navigate(selected.getAttribute("href"));
      } else if (
        searchSelectedIndex >= 0 &&
        searchResults[searchSelectedIndex]
      ) {
        const store = searchResults[searchSelectedIndex];
        recordRecent(storeEntry(store));
        navigate(searchHref(store));
      } else {
        const query = e.target.value.trim();
        if (query) {
          recordRecent({ kind: "query", text: query });
          navigate(`/offers?q=${encodeURIComponent(query)}`);
        }
      }
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const count = getNavigableItems().length;
      if (count === 0) return;
      if (e.key === "ArrowDown") {
        setSelectedIndex(Math.min(searchSelectedIndex + 1, count - 1));
      } else {
        setSelectedIndex(Math.max(searchSelectedIndex - 1, -1));
      }
    }
  });

  // ── #9: Use MutationObserver to reliably track search open/close ──
  // Fragno's internal Escape handler calls closeSearch() without dispatching
  // the toggle event, so we can't rely on it alone. Watch the search-open
  // attribute instead.
  function onSearchStateChange(open) {
    const backdrop = getOrCreateBackdrop();
    const input = globalNav.querySelector(".fragno-global-nav__search-input");
    if (open) {
      // Close profile dropdown and community selector when search opens
      if (profileDropdown?.hasAttribute("open")) {
        profileDropdown.removeAttribute("open");
      }
      const compactDropdown = document.querySelector(
        ".shop-community-selector__compact-dropdown",
      );
      if (compactDropdown) {
        communitySelector?.dispatchEvent(new Event("close-compact-dropdown"));
      }

      backdrop.removeAttribute("hidden");
      globalNav.classList.add("is-search-open");
      document.body.style.overflow = "hidden";
      // #18: Inert everything outside the global nav so Tab stays in the overlay
      document.querySelectorAll("body > *").forEach((el) => {
        if (!el.contains(globalNav) && el !== backdrop) {
          el.setAttribute("inert", "");
          el.setAttribute("data-search-inert", "");
        }
      });
      ensureClearButton();
      // #6: Restore previous query if fragno cleared the input (Escape path)
      if (input && !input.value && lastSearchQuery) {
        input.value = lastSearchQuery;
      }
      // #3: Move cursor to end of restored/existing text
      if (input && input.value.length > 0) {
        requestAnimationFrame(() => {
          input.setSelectionRange(input.value.length, input.value.length);
        });
      }
      syncClearButton();
      // Re-show previous results if input has text
      const query = input?.value.trim() || "";
      if (query.length >= 2 && searchResults.length > 0) {
        renderSearchResults(searchResults);
      } else if (query.length >= 2 && searchResults.length === 0) {
        fetchSearch(query);
      }
      ensureZeroState();
      refreshZeroState();
      syncZeroState(query);
    } else {
      // Reset so the next open re-shows the zero state and counts a new impression.
      if (zeroState) zeroState.el.hidden = true;
      zeroStateImpressionSent = false;
      clearSelection();
      // Save the current input value before fragno clears it
      if (input && input.value.trim()) {
        lastSearchQuery = input.value.trim();
      }
      globalNav.classList.remove("is-search-open");
      document.body.style.overflow = "";
      // #18: Remove inert from elements we marked
      document.querySelectorAll("[data-search-inert]").forEach((el) => {
        el.removeAttribute("inert");
        el.removeAttribute("data-search-inert");
      });
      const localNav = document.querySelector("shop-local-nav");
      if (!localNav?.classList.contains("is-menu-open")) {
        backdrop.setAttribute("hidden", "");
      }
    }
    // Pass `open` explicitly: on the toggle-event path the attribute may not
    // have changed yet.
    syncCombobox(open);
  }

  const searchObserver = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.attributeName === "search-open") {
        onSearchStateChange(globalNav.hasAttribute("search-open"));
      }
    }
  });
  searchObserver.observe(globalNav, {
    attributes: true,
    attributeFilter: ["search-open"],
  });

  // Also handle the event for cases where it IS dispatched (close button click)
  globalNav.addEventListener("fragno-global-nav-search-toggle", (e) => {
    onSearchStateChange(e.detail?.open ?? false);
  });

  // ── Community confirm ──
  const availableEl =
    communitySelector || document.querySelector("[data-available-communities]");
  let hiddenIds = new Set();
  if (availableEl) {
    try {
      const parsed = JSON.parse(
        availableEl.dataset.availableCommunities || "[]",
      );
      hiddenIds = new Set(parsed.filter((c) => c.hidden).map((c) => c.id));
    } catch (e) {
      // malformed — treat as empty
    }
  }

  document.addEventListener("community-confirm", (e) => {
    const committed = e.detail?.communities ?? [];
    const hasVisible = committed.some((id) => !hiddenIds.has(id));
    const banner = document.querySelector(".shop-community-banner");
    if (banner) banner.hidden = hasVisible;
    if (hasVisible) {
      globalNav.setAttribute("show-filters-cta", "");
      const allCommunities = availableEl
        ? (() => {
            try {
              return JSON.parse(
                availableEl.dataset.availableCommunities || "[]",
              );
            } catch {
              return [];
            }
          })()
        : [];
      const iconUrls = committed
        .filter((id) => !hiddenIds.has(id))
        .map((id) => allCommunities.find((c) => c.id === id)?.icon_url)
        .filter(Boolean);
      if (iconUrls.length > 0) {
        globalNav.setAttribute("filters-cta-icons", JSON.stringify(iconUrls));
      } else {
        globalNav.removeAttribute("filters-cta-icons");
      }
    } else {
      globalNav.removeAttribute("show-filters-cta");
      globalNav.removeAttribute("filters-cta-icons");
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initNavWiring);
} else {
  initNavWiring();
}

export { initNavWiring };
